/**
 * Module to do authenticated requests using a stored access and refresh token
 * from the flask jwt library
 * SETUP:
 *   - In main.js, there needs to be a route with the name of 'login'. This
 *      route will be navigated to if there is an unauthenticated request.
 */

import $ from 'jquery'
import Cookies from 'js-cookie'

import router from '../router/index'
import NO_AUTH_ROUTES from '../router/no_auth_routes'
import store from '../store/index'
import * as types from '../store/mutation-types'

// The base url of all calls from this library
const apiURL = '/manage/api/v1.0'
// URL to call to refresh the JSON web token
const refreshTokenURL = '/manage/api/v1.0/auth/refresh_token'
// URL to call to get a fresh access token
const accessTokenURL = '/manage/api/v1.0/auth/access_token'
// Max number of times to retry a failed request because of a timeout
const RETRY_LIMIT = 1
// Number of ms to wait between retrying timed out requests
const RETRY_DELAY = 800

// The title in the response JSON when a fresh access token is required
const FRESH_TOKEN_REQUIRED_TITLE = 'Fresh Access Token Required'

export default {
  /**
   * HTTP GET method using authentication and default base api url
   * - options :
   *    - url : The url to request
   *    - success () : The callback with the results of the request if succesful
   *    - error () : The callback for if there is an error that returned a JSON error
   *    - disableApiUrl : If true, the apiURL will not be appended to the request
   */
  get (options) {
    let self = this
    options = this.setDefaultOptions(options)
    return $.ajax({
      method: 'GET',
      headers: {
        'X-CSRF-TOKEN': Cookies.get('csrf_access_token')
      },
      tryCount: 0,
      url: self.urlHelper(options),
      async: options.async,
      success (result, statusText) {
        self.reqSuccess(result, options)
      },
      error (jqXHR, textStatus, errorThrown) {
        self.reqError(jqXHR,
          textStatus,
          errorThrown,
          options,
          this,
          self.get,
          options)
      }
    })
  },
  /**
   * HTTP POST method
   * - options :
   *    - url : The url to request
   *    - data : The plain data that will be serialized and sent
   *    - success () : The callback with the results of the request if succesful
   *    - error () : The callback for if there is an error that returned a JSON error
   *    - disableApiUrl : If true, the apiURL will not be appended to the request
   */
  post (options) {
    let self = this
    options = this.setDefaultOptions(options)
    return $.ajax({
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': Cookies.get('csrf_access_token'),
        'Content-Type': 'application/json'
      },
      tryCount: 0,
      url: self.urlHelper(options),
      contentType: 'application/json',
      data: JSON.stringify(options.data),
      async: options.async,
      success (result, textStatus) {
        self.reqSuccess(result, options)
      },
      error (jqXHR, textStatus, errorThrown) {
        self.reqError(jqXHR,
          textStatus,
          errorThrown,
          options,
          this,
          self.post,
          options)
      }
    })
  },
  /**
   * HTTP PUT method
   * - options :
   *    - url : The url request
   *    - data : The plain data that will be serialized and sent
   *    - success () : The callback with the results of the request if succesful
   *    - error () : The callback for if there is an error that returned a JSON error
   *    - disableApiUrl : If true, the apiURL will not be appended to the request
   */
  put (options) {
    let self = this
    options = this.setDefaultOptions(options)
    return $.ajax({
      method: 'PUT',
      headers: {
        'X-CSRF-TOKEN': Cookies.get('csrf_access_token'),
        'Content-Type': 'application/json'
      },
      url: self.urlHelper(options),
      tryCount: 0,
      contentType: 'application/json',
      data: JSON.stringify(options.data),
      dataType: 'json',
      async: options.async,
      success (result, textStatus) {
        self.reqSuccess(result, options)
      },
      error (jqXHR, textStatus, errorThrown) {
        self.reqError(jqXHR,
          textStatus,
          errorThrown,
          options,
          this,
          self.put,
          options)
      }
    })
  },
  /**
   * HTTP PATCH method
   * - options :
   *    - url : The url request
   *    - data : The plain data that will be serialized and sent
   *    - success () : The callback with the results of the request if succesful
   *    - error () : The callback for if there is an error that returned a JSON error
   *    - disableApiUrl : If true, the apiURL will not be appended to the request
   */
  patch (options) {
    let self = this
    options = this.setDefaultOptions(options)
    return $.ajax({
      method: 'PATCH',
      headers: {
        'X-CSRF-TOKEN': Cookies.get('csrf_access_token'),
        'Content-Type': 'application/json'
      },
      url: self.urlHelper(options),
      tryCount: 0,
      contentType: 'application/json',
      data: JSON.stringify(options.data),
      dataType: 'json',
      async: options.async,
      success (result, textStatus) {
        self.reqSuccess(result, options)
      },
      error (jqXHR, textStatus, errorThrown) {
        self.reqError(jqXHR,
          textStatus,
          errorThrown,
          options,
          this,
          self.put,
          options)
      }
    })
  },
  /**
   * HTTP DELETE method
   * - options :
   *    - url : The url to request
   *    - success () : The callback with the results of the request if succesful
   *    - error () : The callback for if there is an error that returned a JSON error
   *    - disableApiUrl : If true, the apiURL will not be appended to the request
   */
  delete (options) {
    let self = this
    options = this.setDefaultOptions(options)
    // Set default options
    return $.ajax({
      method: 'DELETE',
      headers: {
        'X-CSRF-TOKEN': Cookies.get('csrf_access_token'),
        'Content-Type': 'application/json'
      },
      tryCount: 0,
      url: self.urlHelper(options),
      data: JSON.stringify(options.data),
      dataType: 'json',
      async: options.async,
      success (result, textStatus) {
        self.reqSuccess(result, options)
      },
      error (jqXHR, textStatus, errorThrown) {
        self.reqError(jqXHR,
          textStatus,
          errorThrown,
          options,
          this,
          self.delete,
          options)
      }
    })
  },
  /**
   * Returns authRequest options with all defaults set
   */
  setDefaultOptions (options) {
    if (!('modalNotifications' in options)) {
      options['modalNotifications'] = false
    }
    if (!('notifications' in options)) {
      options['notifications'] = true
    }
    if (!('async' in options)) {
      options['async'] = true
    }
    return options
  },
  /**
   * Add the notifications from a request based on the given options
   */
  addNotifications (responseJSON, ajaxOptions) {
    // Parse JSON if a string
    if (typeof responseJSON === 'string') {
      try {
        responseJSON = JSON.parse(responseJSON)
      } catch (e) {
        // Failed to parse JSON
        return
      }
    }
    if (ajaxOptions.notifications) {
      store.commit(types.ADD_NOTIFICATION_FROM_RESULT, responseJSON)
    }
    if (ajaxOptions.modalNotifications) {
      store.commit(types.ADD_MODAL_NOTIFICATION_FROM_RESULT, responseJSON)
    }
  },
  /**
   * Success callback for all ajax requests in this module
   */
  reqSuccess (result, ajaxOptions) {
    this.addNotifications(result, ajaxOptions)
    // Call the given success callback
    ajaxOptions.success(result)
  },
  /**
   * Error callback for all ajax requests in this module
   */
  reqError (jqXHR, textStatus, errorThrown, ajaxOptions, ajaxRequest, httpMethod, httpOptions) {
    if (this.handleTimeout(jqXHR, textStatus, ajaxRequest)) {
      // This was a timed out request
      return
    } else if (typeof jqXHR.responseJSON === 'undefined' && errorThrown !== 'abort') {
      this.unhandledException()
    } else if (jqXHR.status !== 401) {
      this.addNotifications(jqXHR.responseJSON, ajaxOptions)
      // Call the given error callback
      if ('error' in ajaxOptions) {
        ajaxOptions.error(jqXHR, textStatus, errorThrown)
      }
    } else if (jqXHR.responseJSON.title === FRESH_TOKEN_REQUIRED_TITLE) {
      this.promptRefreshAccessToken(httpMethod, httpOptions)
    } else {
      this.refreshToken(httpMethod, httpOptions)
    }
  },
  /**
   * Attempts to refresh the access token using the refresh token. If neither
   * is fresh, show the login screen
   */
  refreshToken (ajaxCallback, options) {
    this.refreshTokenRequest().then((result) => {
      // Bind the this variable to the correct context. Otherwise, this inside of
      // ajaxCallback will be undefined
      let boundAjax = ajaxCallback.bind(this)
      // Retry the original request with the new token
      boundAjax(options)
    })
  },
  /**
   * Make the AJAX request to refresh the user's token from stored cookie
   */
  refreshTokenRequest () {
    return new Promise((resolve, reject) => {
      $.ajax({
        context: this,
        type: 'POST',
        data: JSON.stringify({store_in_cookies: true}),
        dataType: 'json',
        tryCount: 0,
        headers: {
          'X-CSRF-TOKEN': Cookies.get('csrf_refresh_token'),
          'Content-Type': 'application/json'
        },
        url: refreshTokenURL,
        success (result) {
          resolve(result)
        },
        error (jqXHR, textStatus, errorThrown) {
          if (this.handleTimeout(jqXHR, textStatus, this)) {
            return
          } else if (jqXHR.status === 401) {
            store.commit(types.LOGGED_OUT)
            store.commit(types.SET_NOTICES, [])
            // Don't navigate away from forgotten password page
            if (!NO_AUTH_ROUTES.includes(router.currentRoute.name)) {
              // Prompt the user that they need to login
              let redirect = sessionStorage.getItem('attemptingRoute')
              if (!redirect) {redirect = router.currentRoute.name}
              sessionStorage.removeItem('attemptingRoute')
              router.push({ name: 'login', query: { timeout: 'true', redirect } })
            }
          } else {
            this.unhandledException()
          }
          // reject()
        }
      })
    })
  },
  /**
   * Prompts the user to re-enter their password to get a fresh access token
   */
  promptRefreshAccessToken (ajaxCallback, options) {
    // Save the request so it can be remade with new credentials
    store.commit(types.SAVE_AJAX_REQUEST, {
      ajaxCallback: ajaxCallback,
      ajaxOptions: options
    })
    // Open the modal for the user to enter their password
    store.commit(types.OPEN_REFRESH_PASSWORD)
  },
  /*
   * Makes the request to get a fresh access token
   */
  refreshAccessToken ({ username, password }) {
    let self = this
    return $.ajax({
      method: 'POST',
      context: this,
      headers: {
        'Content-Type': 'application/json'
      },
      tryCount: 0,
      url: accessTokenURL,
      contentType: 'application/json',
      data: JSON.stringify({
        username: username,
        password: password,
        store_in_cookies: true
      }),
      dataType: 'json',
      success (result, textStatus) {
        let ajaxCallback = store.state.user.ajaxCallback
        let options = store.state.user.ajaxOptions
        // Bind the this variable to the correct context. Otherwise, this inside of
        // ajaxCallback will be undefined
        let boundAjax = ajaxCallback.bind(this)
        // Retry the original request with the new token
        boundAjax(options)
        // TODO redirect back to where the user was or close modal
        store.commit(types.CLEAR_AJAX_REQUEST)
      },
      error (jqXHR, textStatus, errorThrown) {
        // TODO replace options and method with legit data
        let options = {
          test: 'test'
        }
        self.reqError(jqXHR,
          textStatus,
          errorThrown,
          options,
          this,
          self.post,
          options)
      }
    })
  },
  /**
   * Helper that returns the URL to use for the request
   */
  urlHelper (options) {
    if (options.disableApiUrl) {
      return options.url
    }
    else if (options.params){
      return apiURL + options.url + '?' + $.param(options.params);
    }
    else {
      return apiURL + options.url
    }
  },
  /**
   * Test if an error was a timeout and if so retry the request a limited number of times
   * Returns true if there was a timeout and false otherwise.
   */
  handleTimeout (jqXHR, textStatus, ajaxCallback) {
    if (textStatus === 'timeout' ||
          ('responseText' in jqXHR && jqXHR.responseText.includes('Error occurred while trying to proxy to:'))) {
      ajaxCallback.tryCount++
      if (ajaxCallback.tryCount <= RETRY_LIMIT) {
        // Try the request again after the RETRY_DELAY
        window.setTimeout($.ajax, RETRY_DELAY, ajaxCallback)
      } else {
        this.addErrorNotification('Timed out when trying to contact server')
      }
      return true
    } else {
      return false
    }
  },
  /**
   * We give up. An error that wasn't handled happened so display a
   * more general 'Shit went wrong' message
   */
  unhandledException () {
    this.addErrorNotification('An internal server error occurred during the last request')
  },
  /* Handler for success messages. */
  addSuccessNotification (message) {
    store.commit(types.ADD_NOTIFICATION, {
      type: 'success',
      message: message
    })
  },
  /* Handler for error messages. */
  addErrorNotification (message) {
    store.commit(types.ADD_NOTIFICATION, {
      type: 'error',
      message: message
    })
  },
  /* Handler for warning messages   */
  addWarningNotification (message) {
    store.commit(types.ADD_NOTIFICATION, {
      type: 'warning',
      message: message
    })
  },
  /* just a small wrapper of jquerys param function */
  param(url, obj){
    return `${url}${url.endsWith('?') ? '': '?'}${$.param(obj)}`
  }
}



// WEBPACK FOOTER //
// ./src/lib/authRequest.js